import type {StateCreator} from 'zustand'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {DiffEntry, SuggestionIdentifier, SuggestedChangeLineRange} from '@github-ui/diff-lines/conversation-types'
import {
  MAX_SUGGESTED_CHANGES_BATCH_SIZE,
  SUGGESTED_CHANGES_ERRORS,
  validateSuggestedChangeCanBeAddedToBatch,
  type SuggestionForBatchValidation,
} from '@github-ui/diff-lines/helpers/suggested-changes'
import type {CommentSnapshot} from '../../helpers/comment-snapshot'
import {normalizeUnifiedBatchItemUrls} from '../../helpers/unified-batch-url-safety'

/**
 * Single entry in the unified batch panel.
 *
 * Extends {@link SuggestionIdentifier} via the union members so suggestion-aware code
 * can interop with it directly. Membership is keyed on the composite
 * `(commentDatabaseId, suggestionIndex)` — matching the legacy slice — so a human
 * comment with multiple fenced suggestion blocks can have each block added
 * independently.
 */
type UnifiedBatchItemBase = {
  commentGlobalId: string
  commentDatabaseId: number
  path: string
  /**
   * Optional multi-range projection used by CCR suggestions that span multiple hunks
   * (built at the call site via `getLineRangesFromDiffEntries`). When present,
   * overlap detection uses this instead of the single `path`/`lineRange` pair.
   */
  pathLineRanges?: Array<{path: string; lineRange: SuggestedChangeLineRange}>
  isOutdated: boolean
  threadId?: number | null
  /**
   * The thread's `originalDiffPathUri`, captured at add-to-batch time. Only an
   * outdated thread carries one; it points at the diff where the comment was
   * originally made. The panel's "View full comment" uses it to route outdated
   * items to the right diff, since the current `/changes` view may not contain
   * the old hunk (mirrors `useNavigateToThreadComment`).
   */
  sourceUri?: string
  /**
   * Snapshot of the source comment captured at add-to-batch time (`author`,
   * `createdAt`, `bodyHTML`, `bodyTruncated`, `commentUrl`) so the Manage batch
   * panel can render the comment in-place. Built by `buildCommentSnapshot`; does
   * not re-resolve if the source comment is later edited or deleted.
   */
} & CommentSnapshot

/**
 * Discriminated union on `kind` so the compiler keeps the
 * `hasSuggestion` / `suggestionIndex` / `lineRange` combination honest per kind:
 *
 * - `ccr-with-suggestion`: automated review suggestion with a fenced suggestion
 *   block. CCR's legacy contract is one suggestion per comment, hence
 *   `suggestionIndex` is always `undefined`. `lineRange` is the primary hunk and
 *   `pathLineRanges` carries the multi-hunk projection when present.
 * - `ccr-without-suggestion`: automated review finding with no suggestion block
 *   (commentary / observation only). Doesn't participate in overlap validation.
 * - `human-with-suggestion`: human comment with at least one fenced ```suggestion
 *   block; `suggestionIndex` identifies which block within the comment. These
 *   are store-facing items and must have concrete suggestion data.
 * - `human-without-suggestion`: human comment with no suggestion block at all;
 *   doesn't participate in range/overlap validation.
 */
export type UnifiedBatchItem =
  | (UnifiedBatchItemBase & {
      kind: 'ccr-with-suggestion'
      hasSuggestion: true
      suggestionIndex: undefined
      lineRange: SuggestedChangeLineRange | undefined
      /**
       * One entry per file the suggestion touches, passed through from
       * `Comment.automatedComment.suggestion.diffEntries`. Rendered in the panel
       * via `AutomatedReviewDiffEntry` (the same component CCR uses in the thread).
       */
      suggestionFiles: DiffEntry[]
    })
  | (UnifiedBatchItemBase & {
      kind: 'ccr-without-suggestion'
      hasSuggestion: false
      suggestionIndex: undefined
      lineRange: SuggestedChangeLineRange | undefined
    })
  | (UnifiedBatchItemBase & {
      kind: 'human-with-suggestion'
      hasSuggestion: true
      suggestionIndex: number
      lineRange: SuggestedChangeLineRange
      /**
       * The `div.js-suggested-changes-blob` HTML extracted from `bodyHTML` at
       * this row's `suggestionIndex` (the same selector the thread parser uses).
       * Rendered in the panel's Suggestions section via `VerifiedHTMLDiv`.
       */
      suggestionBlobHTML: SafeHTMLString
    })
  | (UnifiedBatchItemBase & {
      kind: 'human-without-suggestion'
      hasSuggestion: false
      suggestionIndex: undefined
      lineRange: undefined
    })

/**
 * Thread-level hint used before a specific human suggestion block is selected.
 * This is only for UI decisions like hiding the row-level batch toggle; it is
 * intentionally not accepted by `addItem`.
 */
export type UnifiedBatchItemThreadHint = UnifiedBatchItemBase & {
  kind: 'human-with-suggestion'
  hasSuggestion: true
  suggestionIndex: undefined
  lineRange: undefined
}

export type UnifiedBatchItemKind = UnifiedBatchItem['kind']

/**
 * Committable = carries a suggestion block (`*-with-suggestion`) and is not
 * outdated. Comment-only items belong to the Fix with Copilot tab; outdated
 * suggestions can no longer be applied at the current head.
 *
 * Single source of truth for "can this item be committed": `getBatchComposition`
 * reads it to gate the Commit CTA and the apply-suggestions mutation reads it to
 * choose which comments to send, so the CTA's enabled state and the committed
 * payload can never drift apart.
 */
export function isCommittableSuggestion(item: UnifiedBatchItem): boolean {
  return item.hasSuggestion && !item.isOutdated
}

/**
 * Per-item outdated status produced by revalidation. Items are matched by the
 * composite `(commentDatabaseId, suggestionIndex)` key; any batched item without a
 * matching entry keeps its current `isOutdated` — revalidation never drops or
 * blindly clears items (fail-safe on empty/partial responses).
 */
export type RevalidatedItemStatus = Pick<SuggestionIdentifier, 'commentDatabaseId' | 'suggestionIndex'> & {
  isOutdated: boolean
}

/**
 * Resolves an item's `isOutdated` during `revalidateItems`.
 *
 * - A matching status is authoritative: use its value.
 * - No match while advancing the baseline: any still-committable item was never validated against
 *   the new head (e.g. it was added mid-refresh, after the caller snapshotted `items`), so mark it
 *   outdated to keep it from committing against an unchecked head.
 * - Otherwise leave the item untouched (revalidation never blindly clears status).
 */
function resolveRevalidatedOutdated(
  item: UnifiedBatchItem,
  status: RevalidatedItemStatus | undefined,
  advanceHead: boolean,
): boolean {
  if (status) return status.isOutdated
  if (advanceHead && isCommittableSuggestion(item)) return true
  return item.isOutdated
}

/** Composite key used for membership/dedup checks. */
const sameItem = (
  a: Pick<SuggestionIdentifier, 'commentDatabaseId' | 'suggestionIndex'>,
  b: Pick<SuggestionIdentifier, 'commentDatabaseId' | 'suggestionIndex'>,
): boolean => a.commentDatabaseId === b.commentDatabaseId && (a.suggestionIndex ?? null) === (b.suggestionIndex ?? null)

export interface UnifiedBatchSlice {
  unifiedBatch: {
    items: UnifiedBatchItem[]
    isPending: boolean
    /**
     * `true` while a hand-off mutation (Fix with Copilot / Commit suggestions) is
     * in flight.
     */
    isSubmitting: boolean
    /**
     * Head OID the batch was last revalidated against, sent as the apply mutation's
     * `expectedHeadOid`. Seeded at page load and advanced by `revalidateItems` on Refresh, so after
     * a push it stays behind the live head until the batch is reclassified — which is what makes the
     * server return `HEAD_OID_MISMATCH` for a stale commit.
     */
    revalidatedHeadSha: string
  }
  unifiedBatchActions: {
    addItem: (item: UnifiedBatchItem) => {ok: true} | {ok: false; reason: string}
    removeItem: (id: SuggestionIdentifier) => void
    removeItemsForComment: (commentDatabaseId: number) => void
    clearBatch: () => void
    /**
     * Locks/unlocks the batch while a submission mutation is in flight.
     */
    setSubmitting: (submitting: boolean) => void
    markOutdated: (id: SuggestionIdentifier) => void
    /**
     * Applies real per-item outdated status on Refresh / restore, matching by the composite
     * `(commentDatabaseId, suggestionIndex)` key. Flips items to outdated and clears ones that are
     * current again; items are never removed, so outdated suggestions stay as Fix-with-Copilot
     * context and an empty or failed response can't drop items.
     *
     * When `revalidatedHeadSha` is supplied (the Refresh path), it also advances the head that
     * commits send as `expectedHeadOid`. Callers must derive `statuses` from data fetched at that
     * head, and should use `deriveRefreshedStatuses` so any committable item the refetch couldn't
     * account for is marked outdated rather than committed against an unverified head.
     */
    revalidateItems: (statuses: RevalidatedItemStatus[], revalidatedHeadSha?: string) => void
    loadFromStorage: (items: UnifiedBatchItem[]) => void
  }
}

export type UnifiedBatchSliceOpts = {
  initialItems?: UnifiedBatchItem[]
  /**
   * Seeds `revalidatedHeadSha`. Production always seeds the page-load head via LayoutStore; the
   * `''` default only applies to slice-level tests that don't exercise the head.
   */
  initialHeadSha?: string
  /**
   * When `false`, every mutator is a no-op and `addItem` returns
   * `{ok: false, reason: 'unified batch is disabled'}`. The state shape is preserved
   * so selectors keep returning safe empty values regardless of the flag.
   */
  enabled?: boolean
}

const DISABLED_REASON = 'unified batch is disabled'
const SUBMITTING_REASON = "can't add to the batch while it's being submitted."

const toValidationShape = (item: UnifiedBatchItem): SuggestionForBatchValidation => ({
  threadId: item.threadId ?? null,
  pathLineRanges:
    item.pathLineRanges && item.pathLineRanges.length > 0
      ? item.pathLineRanges
      : item.lineRange
        ? [{path: item.path, lineRange: item.lineRange}]
        : [],
})

export const createUnifiedBatchSlice =
  (
    opts: UnifiedBatchSliceOpts = {},
  ): StateCreator<UnifiedBatchSlice, [['zustand/devtools', never]], [], UnifiedBatchSlice> =>
  (set, get) => {
    const enabled = opts.enabled ?? true
    const seedItems = enabled ? (opts.initialItems ?? []) : []
    return {
      unifiedBatch: {
        items: seedItems,
        isPending: seedItems.length > 0,
        isSubmitting: false,
        revalidatedHeadSha: opts.initialHeadSha ?? '',
      },
      unifiedBatchActions: {
        addItem: (item: UnifiedBatchItem) => {
          if (!enabled) return {ok: false, reason: DISABLED_REASON}
          if (get().unifiedBatch.isSubmitting) return {ok: false, reason: SUBMITTING_REASON}
          const current = get().unifiedBatch.items
          const alreadyExists = current.some(existing => sameItem(existing, item))
          if (alreadyExists) {
            return {ok: true}
          }

          if (current.length >= MAX_SUGGESTED_CHANGES_BATCH_SIZE) {
            return {ok: false, reason: SUGGESTED_CHANGES_ERRORS.BATCH_TOO_BIG}
          }

          if (item.hasSuggestion) {
            // Pre-check above covers non-suggestion items; here we just validate suggestion-bearing
            // overlap among the suggestion-bearing subset.
            const pending = current.filter(existing => existing.hasSuggestion).map(toValidationShape)
            const validation = validateSuggestedChangeCanBeAddedToBatch(toValidationShape(item), pending)
            if (!validation.isValid) {
              return {ok: false, reason: validation.reason ?? SUGGESTED_CHANGES_ERRORS.INVALID_RANGE}
            }
          }

          set(
            state => {
              const items = [...state.unifiedBatch.items, normalizeUnifiedBatchItemUrls(item)]
              return {unifiedBatch: {...state.unifiedBatch, items, isPending: items.length > 0}}
            },
            undefined,
            'unifiedBatch#addItem',
          )

          return {ok: true}
        },
        removeItem: (id: SuggestionIdentifier) => {
          if (!enabled) return
          set(
            state => {
              const items = state.unifiedBatch.items.filter(item => !sameItem(item, id))
              return {unifiedBatch: {...state.unifiedBatch, items, isPending: items.length > 0}}
            },
            undefined,
            'unifiedBatch#removeItem',
          )
        },
        removeItemsForComment: (commentDatabaseId: number) => {
          if (!enabled) return
          set(
            state => {
              const items = state.unifiedBatch.items.filter(item => item.commentDatabaseId !== commentDatabaseId)
              if (items.length === state.unifiedBatch.items.length) return state
              return {unifiedBatch: {...state.unifiedBatch, items, isPending: items.length > 0}}
            },
            undefined,
            'unifiedBatch#removeItemsForComment',
          )
        },
        clearBatch: () => {
          if (!enabled) return
          set(
            state => ({unifiedBatch: {...state.unifiedBatch, items: [], isPending: false}}),
            undefined,
            'unifiedBatch#clearBatch',
          )
        },
        setSubmitting: (submitting: boolean) => {
          if (!enabled) return
          set(
            state => ({unifiedBatch: {...state.unifiedBatch, isSubmitting: submitting}}),
            undefined,
            'unifiedBatch#setSubmitting',
          )
        },
        markOutdated: (id: SuggestionIdentifier) => {
          if (!enabled) return
          set(
            state => {
              const target = state.unifiedBatch.items.find(item => sameItem(item, id))
              if (!target || target.isOutdated) return state
              const items = state.unifiedBatch.items.map(item =>
                sameItem(item, id) ? {...item, isOutdated: true} : item,
              )
              return {unifiedBatch: {...state.unifiedBatch, items}}
            },
            undefined,
            'unifiedBatch#markOutdated',
          )
        },
        revalidateItems: (statuses: RevalidatedItemStatus[], revalidatedHeadSha?: string) => {
          if (!enabled) return
          set(
            state => {
              const current = state.unifiedBatch
              // Refresh supplies the head it fetched from; the reactive reconciler omits it and only
              // refines per-item status. Callers must classify every committable item against that
              // head (see `deriveRefreshedStatuses`) before advancing the baseline.
              const advanceHead = revalidatedHeadSha !== undefined && revalidatedHeadSha !== current.revalidatedHeadSha
              let items = current.items
              if (current.items.length > 0 && (statuses.length > 0 || advanceHead)) {
                let changed = false
                const next = current.items.map(item => {
                  const status = statuses.find(candidate => sameItem(item, candidate))
                  const nextOutdated = resolveRevalidatedOutdated(item, status, advanceHead)
                  if (nextOutdated === item.isOutdated) return item
                  changed = true
                  return {...item, isOutdated: nextOutdated}
                })
                if (changed) items = next
              }
              if (items === current.items && !advanceHead) return state
              return {
                unifiedBatch: {
                  ...current,
                  items,
                  revalidatedHeadSha: advanceHead ? revalidatedHeadSha : current.revalidatedHeadSha,
                },
              }
            },
            undefined,
            'unifiedBatch#revalidateItems',
          )
        },
        loadFromStorage: (items: UnifiedBatchItem[]) => {
          if (!enabled) return
          set(
            state => ({unifiedBatch: {...state.unifiedBatch, items, isPending: items.length > 0}}),
            undefined,
            'unifiedBatch#loadFromStorage',
          )
        },
      },
    }
  }
